import _ from 'lodash';
import { DateTime } from 'luxon';

import { SUPPORTED_EXTERNAL_ORIGINS, TRIP_STATUS_CODES } from '@/constants';

export const RESTART_ON_REMOUNT = '@@saga-injector/restart-on-remount';
export const DAEMON = '@@saga-injector/daemon';
export const ONCE_TILL_UNMOUNT = '@@saga-injector/once-till-unmount';

export const CANCELLABLE_STATUSES = {
  APPROVAL_PENDING: 'APPROVAL_PENDING',
  ASSIGNMENT_PENDING: 'ASSIGNMENT_PENDING',
  ASSIGNMENT_ACCEPTED: 'ASSIGNMENT_ACCEPTED',
  ASSIGNMENT_ERROR: 'ASSIGNMENT_ERROR',
  // ASSIGNMENT_REJECTED: 'ASSIGNMENT_REJECTED',
  ASSIGNMENT_UNAVAILABLE: 'ASSIGNMENT_UNAVAILABLE',
  ASSIGNMENT_DISPATCHED: 'ASSIGNMENT_DISPATCHED',
  EN_ROUTE: 'EN_ROUTE',
  ARRIVED_AT_PU: 'ARRIVED_AT_PU',
  READY_TO_ASSIGN: 'READY_TO_ASSIGN',
  ASSIGNMENT_EXPORTED: 'ASSIGNMENT_EXPORTED',
};

export const VALID_ASSIGNABLE_STATUS_LIST = [
  TRIP_STATUS_CODES.READY_TO_ASSIGN,
  TRIP_STATUS_CODES.ASSIGNMENT_PENDING,
  TRIP_STATUS_CODES.ASSIGNMENT_EXPORTED,
  TRIP_STATUS_CODES.ASSIGNMENT_ACCEPTED,
  TRIP_STATUS_CODES.ASSIGNMENT_DISPATCHED,
];

export const VALID_RE_ASSIGNABLE_STATUS_LIST = [
  TRIP_STATUS_CODES.ASSIGNMENT_ERROR,
  TRIP_STATUS_CODES.ASSIGNMENT_ACCEPTED,
  TRIP_STATUS_CODES.ASSIGNMENT_REJECTED,
  TRIP_STATUS_CODES.ASSIGNMENT_CANCELLED,
  TRIP_STATUS_CODES.ASSIGNMENT_UNAVAILABLE,
  TRIP_STATUS_CODES.ASSIGNMENT_DISPATCHED,
];

export const TRIP_COMPLETED_STATUSES = [TRIP_STATUS_CODES.COMPLETED];

export const TRIP_UPCOMING_STATUSES = [
  TRIP_STATUS_CODES.APPROVAL_PENDING,
  TRIP_STATUS_CODES.READY_TO_ASSIGN,
  TRIP_STATUS_CODES.ASSIGNMENT_PENDING,
  TRIP_STATUS_CODES.ASSIGNMENT_EXPORTED,
  TRIP_STATUS_CODES.ASSIGNMENT_ACCEPTED,
];

export const DENIABLE_STATUSES = {
  READY_TO_ASSIGN: 'READY_TO_ASSIGN',
  ASSIGNMENT_EXPORTED: 'ASSIGNMENT_EXPORTED',
  ASSIGNMENT_ACCEPTED: 'ASSIGNMENT_ACCEPTED',
  ASSIGNMENT_DISPATCHED: 'ASSIGNMENT_DISPATCHED',
  APPROVAL_PENDING: 'APPROVAL_PENDING', // New trip details has approve and deny feature from trip details page
};

export const PROVIDER_TYPE = {
  RENDERING: 'renderingProvider',
  REFERRING: 'referringProvider',
  ORDERING: 'orderingProvider',
  SUPERVISING: 'supervisingProvider',
};

export const VALID_ROLES = {
  USER: 'user',
  ADMIN: 'admin',
};

export const VALID_PERMISSIONS = {
  READ: 'read_roles',
  WRITE: 'write_roles',
  INVITE: 'invite_users',
  REMOVE: 'remove_users',
  WRITE_CONFIGS: 'write_configs',
};

export const GENDER_OPTIONS = {
  M: 'Male',
  F: 'Female',
  U: 'N/A',
};

export const GENDER_DROPDOWN_OPTIONS = Object.keys(GENDER_OPTIONS).map(
  (key) => ({
    value: key,
    label: GENDER_OPTIONS[key],
  }),
);

/**
 * Generates time remaining dropdown options with fresh time ranges from now
 * @returns {Array<{value: [string, string], label: string}>} Array of dropdown options
 */
export const TIME_REMAINING_DROPDOWN_OPTIONS = (() => {
  const now = DateTime.now().toUTC();
  const in30m = now.plus({ minutes: 30 });
  const in60m = now.plus({ minutes: 60 });
  const in120m = now.plus({ minutes: 120 });
  const in240m = now.plus({ minutes: 240 });
  const in12h = now.plus({ hours: 12 });
  const in24h = now.plus({ hours: 24 });
  const in3d = now.plus({ days: 3 });
  const in7d = now.plus({ days: 7 });
  const in4w = now.plus({ weeks: 4 });
  const in3mo = now.plus({ months: 3 });
  const farFuture = DateTime.fromObject(
    { year: 2999, month: 12, day: 31 },
    { zone: 'utc' },
  );

  return [
    {
      value: [
        now.toUTC(0, { keepLocalTime: false }).toISO(),
        in30m.toUTC(0, { keepLocalTime: false }).toISO(),
      ],
      label: '<30m',
    },
    {
      value: [
        in30m.toUTC(0, { keepLocalTime: false }).toISO(),
        in60m.toUTC(0, { keepLocalTime: false }).toISO(),
      ],
      label: '30-60m',
    },
    {
      value: [
        in60m.toUTC(0, { keepLocalTime: false }).toISO(),
        in120m.toUTC(0, { keepLocalTime: false }).toISO(),
      ],
      label: '1-2h',
    },
    {
      value: [
        in120m.toUTC(0, { keepLocalTime: false }).toISO(),
        in240m.toUTC(0, { keepLocalTime: false }).toISO(),
      ],
      label: '2-4h',
    },
    {
      value: [
        in240m.toUTC(0, { keepLocalTime: false }).toISO(),
        in12h.toUTC(0, { keepLocalTime: false }).toISO(),
      ],
      label: '4-12h',
    },
    {
      value: [
        in12h.toUTC(0, { keepLocalTime: false }).toISO(),
        in24h.toUTC(0, { keepLocalTime: false }).toISO(),
      ],
      label: '12-24h',
    },
    {
      value: [
        in24h.toUTC(0, { keepLocalTime: false }).toISO(),
        in3d.toUTC(0, { keepLocalTime: false }).toISO(),
      ],
      label: '1-3d',
    },
    {
      value: [
        in3d.toUTC(0, { keepLocalTime: false }).toISO(),
        in7d.toUTC(0, { keepLocalTime: false }).toISO(),
      ],
      label: '3-7d',
    },
    {
      value: [
        in7d.toUTC(0, { keepLocalTime: false }).toISO(),
        in4w.toUTC(0, { keepLocalTime: false }).toISO(),
      ],
      label: '1-4w',
    },
    {
      value: [
        in4w.toUTC(0, { keepLocalTime: false }).toISO(),
        in3mo.toUTC(0, { keepLocalTime: false }).toISO(),
      ],
      label: '1-3mo',
    },
    {
      value: [
        in3mo.toUTC(0, { keepLocalTime: false }).toISO(),
        farFuture.toUTC(0, { keepLocalTime: false }).toISO(),
      ],
      label: '>3mo',
    },
  ];
})();

export const TRIP_SOURCE_NAMES = {
  [SUPPORTED_EXTERNAL_ORIGINS.KINETIK_MA.name]: 'true',
  System: 'false',
};

export const TRIP_SOURCE_DROPDOWN_OPTIONS = Object.entries(
  TRIP_SOURCE_NAMES,
).map(([key, value]) => ({
  value,
  label: key,
}));

export const COVID_STTAUS = {
  Positive: 'Positive',
  Negative: 'Negative',
  Unknown: 'Unknown',
};

export const COVID_STATUS_DROPDOWN_OPTIONS = Object.keys(COVID_STTAUS).map(
  (key) => ({
    value: key,
    label: COVID_STTAUS[key],
  }),
);

export const BOOLEAN_VALUE_TO_LABEL = {
  true: 'Yes',
  false: 'No',
};

export const CONSENT_DROPDOWN_OPTIONS = [
  {
    label: 'Yes',
    value: 'yes',
  },
  {
    label: 'No',
    value: 'no',
  },
  {
    label: 'N/A',
    value: 'n/a',
  },
];

export const TABLE_TOOLS = {
  PATIENT: 'PATIENT',
  TRIP: 'TRIP',
  NOTIFICATION: 'NOTIFICATION',
};

export const PAYER_ID_TYPES = {
  FI: "Federal Taxpayer's ID",
  // 24: 'Employers Identification Number',
};

export const REVERSED_PAYER_ID_TYPES = {
  "Federal Taxpayer's ID": 'FI',
  // 'Employers Identification Number': '24',
};

export const MEMBER_RELATIONS = {
  Mother: 'Mother',
  Father: 'Father',
  Spouse: 'Spouse',
  Child: 'Child',
  Sibling: 'Sibling',
  Cousin: 'Cousin',
  Relative: 'Relative',
  Partner: 'Partner',
  Guardian: 'Guardian',
  CareCoordinator: 'Care Coordinator',
  Other: 'Other',
  Self: 'Self',
};

export const SPECIAL_NEEDS = {
  AMBULANCE: 'Ambulance',
  BARIATRIC: 'Bariatric',
  CANE: 'Cane',
  COLLAPSIBLE_WALKER: 'Collapsible Walker',
  DIALYSIS_MACHINE: 'Dialysis Machine',
  FOLDING_WHEELCHAIR: 'Folding Wheelchair',
  GURNEY: 'Gurney',
  HEARING_IMPAIRED: 'Hearing Impaired',
  MOTORIZED_WHEELCHAIR: 'Motorized Wheelchair',
  OXYGEN_TANK: 'Oxygen Tank',
  RESPIRATORS: 'Respirators',
  SERVICE_ANIMAL: 'Service Animal',
  STRETCHER: 'Stretcher',
  VISION_IMPAIRED: 'Vision Impaired',
  WHEELCHAIR: 'Wheelchair',
};

export const TRIP_STATUS_CODE_STYLES = {
  APPROVAL_PENDING: {
    title: 'Approval Pending',
    background: '#D7F4F7',
    opacity: 0.8,
    color: '#09C1D6',
  },
  APPROVAL_DENIED: {
    title: 'Approval Denied',
    background: '#FFEEE3',
    opacity: 0.8,
    color: '#FF8b33',
  },
  READY_TO_ASSIGN: {
    title: 'Ready to Assign',
    background: '#DCF2FF',
    opacity: 0.8,
    color: '#0090F9',
  },
  ASSIGNMENT_PENDING: {
    title: 'Assignment Pending',
    background: '#E3E2EF',
    opacity: 0.8,
    color: '#716DAE',
    size: '0.85rem',
  },
  ASSIGNMENT_ACCEPTED: {
    title: 'Accepted',
    color: '#1C9965',
    background: '#E3FCEEB2',
    opacity: 0.1,
    size: '0.85rem',
  },
  ASSIGNMENT_EXPORTED: {
    title: 'Assignment Booked',
    background: '#eae9ff',
    color: '#504CFF',
    opacity: 0.6,
    size: '0.85rem',
  },
  ASSIGNMENT_ERROR: {
    title: 'Assignment Error',
    background: '#FDDCDF',
    color: '#F35261',
    opacity: 0.6,
    size: '0.85rem',
  },
  ASSIGNMENT_REJECTED: {
    title: 'Assignment Rejected',
    background: '#F7EBE2',
    color: '#FE674A',
    opacity: 0.8,
    size: '0.85rem',
  },
  ASSIGNMENT_CANCELLED: {
    title: 'Assignee Cancelled',
    background: '#C501361A',
    color: '#C50136',
    opacity: 0.1,
    size: '0.85rem',
  },
  ASSIGNMENT_UNAVAILABLE: {
    title: 'Assignee Unavailable',
    background: '#C597061A',
    color: '#C59706',
    opacity: 0.1,
    size: '0.85rem',
  },
  REQUESTER_CANCELLED: {
    title: 'Requester Cancelled',
    background: '#B538C91A',
    color: '#B538C9',
    opacity: 0.1,
    size: '0.85rem',
  },
  PATIENT_CANCELLED: {
    title: 'Member Cancelled',
    background: '#9A41091A',
    color: '#9A4109',
    opacity: 0.1,
    size: '0.85rem',
  },
  NO_SHOW: {
    title: 'Member No Show',
    background: '#F7F7F8',
    color: '#83858F',
    size: '0.85rem',
  },
  PROVIDER_NO_SHOW: {
    title: 'Provider No Show',
    background: '#C501361A',
    color: '#C50136',
    size: '0.85rem',
  },
  ASSIGNMENT_DISPATCHED: {
    title: 'Assignee Dispatched',
    background: '#3C534730',
    color: '#3C5347',
    size: '0.85rem',
    opacity: 0.1,
  },
  EN_ROUTE: {
    title: 'Trip In Progress',
    background: '#2248881A',
    color: '#224888',
    opacity: 0.1,
    size: '0.85rem',
  },
  ARRIVED_AT_PU: {
    title: 'Trip In Progress',
    background: '#08D8021A',
    color: '#08D802',
    opacity: 0.1,
    size: '0.85rem',
  },
  POB: {
    title: 'Trip In Progress',
    background: '#5423561A',
    color: '#542356',
    opacity: 0.1,
    size: '0.85rem',
  },
  COMPLETED: {
    title: 'Completed',
    background: '#E3FCEE',
    color: '#1C9965',
    size: '0.85rem',
  },
  EXPIRED: {
    title: 'Expired',
    background: '#C501361A',
    color: '#C50136',
    size: '0.85rem',
  },
  DENIED: {
    title: 'Denied',
    background: '#C501361A',
    color: '#C50136',
    size: '0.85rem',
  },
};

export const WEEKDAYS = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
};

export const FREQUENCY_KEY_MAP = {
  Days: 'DAILY',
  Weeks: 'WEEKLY',
  Months: 'MONTHLY',
};

// Update FREQUENCY_KEY_MAP if any changes needed
export const TRIP_FREQUENCY = {
  Day: 'Days',
  Week: 'Weeks',
  Month: 'Months',
};

export const TABLE_ROW_HEIGHT = 52;

export const ACTION_ALERTS = {
  ALERT: 'ALERT',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
};

export const TimezoneList = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
];

export const TimezoneLabels = {
  'America/New_York': 'Eastern Time (US)',
  'America/Chicago': 'Central Time (US)',
  'America/Denver': 'Mountain Time (US)',
  'America/Los_Angeles': 'Pacific Time (US)',
};
export const TNC_PROVIDERS = { UBER: 'UBER', LYFT: 'LYFT' };
export const TNC_ACCOUNT_MODEL_KEYS = {
  OWN_INTEGRATION: 'OWN_INTEGRATION',
  VIA_KINETIK: 'VIA_KINETIK',
};

export const INDUSTRY_SEGMENT_TAXONOMY_CODE_LABELS = {
  '3416L0300X': 'Land Ambulance',
  '341600000X': 'Ambulance',
  '347B00000X': 'Bus',
  '343900000X': 'Non-Emergency Medical Transport (VAN)',
  '347C00000X': 'Private Vehicle',
  '343800000X': 'Secured Medical Transport (VAN)',
  '344600000X': 'Taxi',
  '347E00000X': 'Transportation Broker',
  '342000000X': 'Transportation Network Company (Uber, Lyft, Via, etc...)',
};

export const tripClassificationCodes = {
  professional: 'Professional',
  // institutional: 'Institutional',
  'non-insurance': 'Non-Insurance',
};

export const contractTypeCodes = {
  PROFESSIONAL_TRANSPORTATION_PROVIDER: 'PROFESSIONAL_TRANSPORTATION_PROVIDER',
  IN_NETWORK: 'IN_NETWORK',
  OUT_OF_NETWORK: 'OUT_OF_NETWORK',
  VOLUNTEER: 'VOLUNTEER',
};

export const BOOKING_TYPE_MAP = {
  SCHEDULED: 'SCHEDULED',
  APPOINTMENT: 'ARRIVE_BY',
  ON_DEMAND: 'ONDEMAND',
  FLEXIBLE: 'WILLCALL',
};

export const BOOKING_TYPE_FE_KEY_MAP = {
  SCHEDULED: 'SCHEDULED',
  ARRIVE_BY: 'APPOINTMENT',
  ONDEMAND: 'ON_DEMAND',
  WILLCALL: 'FLEXIBLE',
};

export const LOCATION_FIELD_KEYS = {
  PICK_UP: 'tripSchedule.pickup',
  DROP_OFF: 'tripSchedule.dropOff',
};

export const TEXTFIELD_OUTLINE_STYLE = {
  styleOverrides: {
    root: {
      '.MuiOutlinedInput-notchedOutline': {
        borderColor: '#EAEAEA',
      },
      '.MuiOutlinedInput-root': {
        color: '#08193E',
        fontSize: '14px',
      },
      '.MuiChip-label': {
        color: '#08193E',
      },
      '.MuiInputLabel-root': {
        color: '#83858F',
        fontSize: '14px',
      },
    },
  },
};

export const INPUT_OUTLINED_STYLE = {
  styleOverrides: {
    root: {
      '.MuiInputBase-root': {
        fontFamily: 'Roboto, sans-serif !important',
        paddingLeft: '1px',
      },
    },
  },
};

export const BUTTON_STYLE = {
  styleOverrides: {
    root: {
      textTransform: 'none !important',
      fontFamily: 'Roboto, sans-serif !important',
    },
  },
};

export const ROUND_MILES_VALUES = {
  CEILING: 'ceiling',
  FLOOR: 'floor',
  NONE: 'none',
};

export const DATE_FIELD_MASK = '__/__/____';

export const LOS_APPOINTMENT_REASON_DROPDOWN_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: '25vh',
      maxWidth: 200,
    },
  },
};

export const COMPLETABLE_STATUSES = {
  COMPLETED: 'COMPLETED',
  ASSIGNMENT_EXPORTED: 'ASSIGNMENT_EXPORTED',
};

export const COMPLETABLE_TRIP_TYPES = {
  MILEAGE_REIMBURSEMENT: 'MILEAGE_REIMBURSEMENT',
  PUBLIC_TRANSPORTATION: 'PUBLIC_TRANSPORTATION',
  ANCILLARY_SERVICES: 'ANCILLARY_SERVICES',
};

export const EDITABLE_STATUSES = {
  READY_TO_ASSIGN: 'READY_TO_ASSIGN',
  ASSIGNMENT_ACCEPTED: 'ASSIGNMENT_ACCEPTED',
  ASSIGNMENT_DISPATCHED: 'ASSIGNMENT_DISPATCHED',
  APPROVAL_PENDING: 'APPROVAL_PENDING',
};

export const TRIP_APPROVAL_REQUEST_STATUS_CODES = {
  OPEN: 'OPEN',
  APPROVED: 'APPROVED',
  DENIED: 'DENIED',
};

export const TRIP_EDIT_DELETABLE_KEYS = {
  referringProvider: 'request.referringProvider',
  renderingProvider: 'request.renderingProvider',
  orderingProvider: 'request.orderingProvider',
  supervisingProvider: 'request.supervisingProvider',
};

export const APPROVAL_REQUEST_ACTIONS = {
  REQUEST_APPROVAL: 'REQUEST_APPROVAL',
  IMMEDIATE_DENIAL: 'IMMEDIATE_DENIAL',
};

export const PARENT_DOCUMENT_COLLECTION = {
  TRIPS: 'trips',
  RATES: 'rates',
  CONTRACTS: 'contracts',
};

export const CONTRACT_STATUS_CODES = {
  A: 'Active',
  P: 'Pending',
  IA: 'Inactive',
};

// Date/time format strings are sourced from `src/constants/dates` and
// re-exported here for backwards compatibility with legacy consumers that
// still import from `utils/constants`. New code should import from
// `@/constants` (or `@/constants/dates`) directly.
export {
  DATE_AT_TIME_FORMAT,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  TIME_FORMAT,
} from '@/constants/dates';

export const MEMBER_EDIT_DELETABLE_KEYS = {
  weightInLbs: 'weightInLbs',
  heightInInches: 'heightInInches',
  mobilePhoneNumber: 'contactInfo.mobilePhoneNumber',
  homePhoneNumber: 'contactInfo.homePhoneNumber',
  workPhoneNumber: 'contactInfo.workPhoneNumber',
  unspecifiedPhoneNumber: 'contactInfo.unspecifiedPhoneNumber',
  faxNumber: 'contactInfo.faxNumber',
  email: 'contactInfo.email',
  middleName: 'name.middleName',
};

export const TRIP_TYPE_NAMES = {
  SCHEDULED: 'Scheduled',
  MILEAGE_REIMBURSEMENT: 'Gas Mileage Reimbursement',
  PUBLIC_TRANSPORTATION: 'Public Transport',
  ANCILLARY_SERVICES: 'Ancillary Services', // 'Meals, Lodging & Flights',
};

export const TRIP_TYPE_DROPDOWN_OPTIONS = Object.keys(TRIP_TYPE_NAMES).map(
  (key) => ({
    value: key,
    label: TRIP_TYPE_NAMES[key],
  }),
);

export const RECURRING_ELIGIBLE_TRIP_TYPE_OPTIONS = Object.keys(
  _.omit(TRIP_TYPE_NAMES, 'ANCILLARY_SERVICES'),
).map((key) => ({
  value: key,
  label: TRIP_TYPE_NAMES[key],
}));

export const BOOKING_TYPE_KEYS = {
  ARRIVE_BY: 'ARRIVE_BY',
  SCHEDULED: 'SCHEDULED',
  ONDEMAND: 'ONDEMAND',
  WILLCALL: 'WILLCALL',
};

export const BOOKING_TYPE_TIME_LABELS = {
  ARRIVE_BY: 'Appointment Time',
  SCHEDULED: 'Pickup Time',
  ONDEMAND: 'On Demand',
  WILLCALL: 'Will Call',
};

export const BOOKING_TYPE_DROPDOWN_OPTIONS = Object.keys(
  BOOKING_TYPE_TIME_LABELS,
).map((key) => ({
  value: key,
  label: BOOKING_TYPE_TIME_LABELS[key],
}));

export const BOOKING_TYPE_LABELS = {
  ARRIVE_BY: 'Arrive By',
  SCHEDULED: 'Scheduled',
  ONDEMAND: 'On Demand',
  WILLCALL: 'Will Call',
};

export const BOOKING_TYPE_VALUES = {
  SCHEDULED: 'SCHEDULED',
  APPOINTMENT: 'APPOINTMENT',
  ON_DEMAND: 'ON_DEMAND',
  FLEXIBLE: 'FLEXIBLE',
};

export const CONFLICT_ELIGIBILE_STATUSES = [
  TRIP_STATUS_CODES.APPROVAL_PENDING,
  TRIP_STATUS_CODES.READY_TO_ASSIGN,
  TRIP_STATUS_CODES.ASSIGNMENT_PENDING,
  TRIP_STATUS_CODES.ASSIGNMENT_ACCEPTED,
  TRIP_STATUS_CODES.ASSIGNMENT_EXPORTED,
  TRIP_STATUS_CODES.ASSIGNMENT_ERROR,
  TRIP_STATUS_CODES.ASSIGNMENT_REJECTED,
  TRIP_STATUS_CODES.ASSIGNMENT_CANCELLED,
  TRIP_STATUS_CODES.ASSIGNMENT_UNAVAILABLE,
  TRIP_STATUS_CODES.ASSIGNMENT_DISPATCHED,
  TRIP_STATUS_CODES.EN_ROUTE,
  TRIP_STATUS_CODES.ARRIVED_AT_PU,
  TRIP_STATUS_CODES.POB,
];

export const MEAN_EARTH_RADIUS = 3958.76; // in miles

export const SECONDS_PER_HOUR = 3600;

// This the HARD-CODED UUID for the "Unspecified" level of service placeholder value
// TODO: Remove when a more scalable solution is implemented
export const UNSPECIFIED_LEVEL_OF_SERVICE_UUID =
  '550e8400-e29b-41d4-a716-446655440000';

/**
 * The number of minutes of user inactivity before the user is logged out
 */
export const APP_INACTIVITY_MIN_THRESHOLD = 10;

// Industry segment codes for types of organizations
export const INDUSTRY_SEGMENT_CODES = {
  MCO: 'MCO', // Managed Care Organization
  TB: 'TB', // Transportation Broker
  HP: 'HP', // Health Plan
  TP: 'TP', // Transportation Provider
};

/**
 * Configuration for TP Contract Billable Items Generation
 * This configuration defines the methods for generating billable items in a TP contract.
 */
export const PROVIDER_CONTRACT_BILLABLE_ITEMS_GENERATION_CONFIG = {
  contractRates: 'contract.rates',
  assigneeQuotedFare: 'assignee.quotedFare',
};

export const METER_TO_MILE = 1609.34;

export const PROVIDER_NO_SHOW_ELIGIBLE_STATUSES = Object.freeze([
  TRIP_STATUS_CODES.EN_ROUTE,
  TRIP_STATUS_CODES.ASSIGNMENT_ACCEPTED,
  TRIP_STATUS_CODES.ASSIGNMENT_DISPATCHED,
]);
